import React from 'react';
import {
	type ProjectType,
	SOFTWARE_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import { ButtonGroup } from '@atlaskit/button';
import { Stack } from '@atlaskit/primitives/compiled';
import { isFedrampModerate, isIsolatedCloud } from '@atlaskit/atlassian-context';
import { fg } from '@atlassian/jira-feature-gating';
import { PageTopBar } from '@atlassian/jira-horizontal-nav-page-topbar/src/ui/index.tsx';
import { useIntl, type IntlShape } from '@atlassian/jira-intl';
import type { AnalyticsData } from '../../state/analytics/types.tsx';
import type { InitiateAction } from '../../state/ui/add/actions.tsx';
import { CompassEmptyState } from '../compass-empty-state/index.tsx';
import { CompassTableCrossflowTouchPointWithEC } from '../compass-table-crossflow/index.tsx';
import CompassComponentsTable from '../compass-table/index.tsx';
import { RequestAccessState } from '../compass-table/request-access-state/index.tsx';
import CreateComponentButton from '../create-component-button/index.tsx';
import EmptyPlaceholder from '../empty-placeholder/index.tsx';
import Error from '../error/index.tsx';
import Filter from '../filter/index.tsx';
import { WrappedGlobalComponentsToggleButton } from '../global-components-toggle-button/index.tsx';
import { LoadingState } from '../loading-state/loading-state.tsx';
import Paginator from '../paginator/index.tsx';
import ComponentsTable from '../table/index.tsx';
import { HeaderBottombarContent } from './header-bottombar-content/index.tsx';
import { messages } from './messages.tsx';
import type { Status } from './selectors.tsx';

type StatusWithLoading = Status | 'loading';

export type Props = {
	intl: IntlShape;
	onAddComponent: () => InitiateAction;
	editable: boolean;
	status: Status;
	analyticsData: AnalyticsData;
	projectKey: string;
	projectName: string;
	projectType: ProjectType;
	isAdminContext: boolean;
	isLoading: boolean;

	globalComponentsProperty?: {
		loading: boolean;
		enabled: boolean;
		hasCompassPermissions: boolean;
		setProperty: (newValue: boolean) => void;
	};
};

export type PropsNew = {
	onAddComponent: () => InitiateAction;
	editable: boolean;
	status: Status;
	analyticsData: AnalyticsData;
	projectKey: string;
	projectType: ProjectType;
	isLoading: boolean;
	globalComponentsProperty?: {
		loading: boolean;
		enabled: boolean;
		hasCompassPermissions: boolean;
		setProperty: (newValue: boolean) => void;
	};
};

function AppViewNew(props: PropsNew) {
	const {
		onAddComponent,
		status,
		editable,
		projectKey,
		projectType,
		isLoading,
		globalComponentsProperty = {
			loading: false,
			enabled: false,
			hasCompassPermissions: false,
			// Replace with lodash/noop
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			setProperty: () => {},
		},
	} = props;

	const { formatMessage } = useIntl();
	const isProjectTypeJsw = projectType === SOFTWARE_PROJECT;
	let content = null;
	let searchBar = null;
	let createComponentButton = null;

	const isHideGlobalComponentsInIsolatedCloud =
		isIsolatedCloud() && fg('jira_fe_hide_components_list');

	const toggleButton =
		isProjectTypeJsw && !isFedrampModerate() && !isHideGlobalComponentsInIsolatedCloud ? (
			<WrappedGlobalComponentsToggleButton
				globalComponentsProperty={{
					loading: globalComponentsProperty.loading,
					enabled: globalComponentsProperty.enabled,
					setProperty: globalComponentsProperty.setProperty,
				}}
				isEditable={editable}
			/>
		) : null;
	let actions = <ButtonGroup>{toggleButton}</ButtonGroup>;

	const statusWithLoading: StatusWithLoading =
		isLoading || globalComponentsProperty.loading ? 'loading' : status;

	switch (statusWithLoading) {
		case 'loading': {
			content = <LoadingState />;
			break;
		}

		case 'empty': {
			if (
				!isFedrampModerate() &&
				!isHideGlobalComponentsInIsolatedCloud &&
				globalComponentsProperty.enabled
			) {
				content = <CompassEmptyState projectKey={projectKey} />;
			} else {
				content = <EmptyPlaceholder projectType={projectType} />;
			}
			break;
		}

		case 'has-components': {
			if (editable) {
				createComponentButton = (
					<CreateComponentButton
						testId="project-directories.components.content.create-component-button"
						loading={globalComponentsProperty.loading}
						globalComponentsEnabled={globalComponentsProperty.enabled}
						oldOnAddComponent={onAddComponent}
						projectKey={projectKey}
					/>
				);
			}

			// check if a user has access to compass, if not show request access state
			if (
				!isFedrampModerate() &&
				!isHideGlobalComponentsInIsolatedCloud &&
				globalComponentsProperty.enabled
			) {
				if (!globalComponentsProperty.hasCompassPermissions) {
					content = <RequestAccessState />;
				} else {
					// FF on - Compass components
					content = (
						<CompassComponentsTable
							testId="project-directories.components.content.compass-components-table"
							projectKey={projectKey}
						>
							<Paginator />
						</CompassComponentsTable>
					);
				}
			}
			// Default / FF off - old Jira components
			else {
				content = (
					<>
						<ComponentsTable />
						<Paginator />
						<CompassTableCrossflowTouchPointWithEC />
					</>
				);
				searchBar = <Filter ariaLabel={formatMessage(messages.filterInputLabel)} />;
			}

			actions = (
				<ButtonGroup>
					{toggleButton}
					{createComponentButton}
				</ButtonGroup>
			);

			break;
		}

		case 'error':
			content = <Error />;
			break;

		default: {
			break;
		}
	}

	return (
		<div data-testId="project-directories.components.content">
			<PageTopBar
				actions={actions}
				bottomBar={
					isProjectTypeJsw ? (
						<Stack grow="fill">
							<HeaderBottombarContent
								isGlobalComponentsEnabled={globalComponentsProperty.enabled}
								searchBar={searchBar}
								formatMessage={formatMessage}
							/>
						</Stack>
					) : (
						searchBar
					)
				}
			/>
			{content}
		</div>
	);
}

const AppView: React.FC<
	PropsNew &
		Pick<
			Omit<Props, 'intl'>,
			| 'onAddComponent'
			| 'status'
			| 'editable'
			| 'projectKey'
			| 'projectName'
			| 'projectType'
			| 'isAdminContext'
			| 'isLoading'
			| 'globalComponentsProperty'
			| 'analyticsData'
		> & {} & {} & {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			forwardedRef?: React.Ref<any>;
		}
> = AppViewNew;
export default AppView;
